import { API_XBI4_URL } from "../../config";
import { getOrgUserLeaveHistory } from "../LeaveManagement/getOrgUserLeaveHistory";
import { getIndEmployeePay } from "../Payslip/getIndividulaEmployeePay";
import { createUser } from "../users/createUser";
import { getUsers } from "../users/getUsers";

export type InsytixEmployee = {
  employee_id: string;
  org_user_id?: string | null;
  Name: string;
  dob?: string | null;
  doj?: string | null;
  p_mail?: string | null;
  c_mail?: string | null;
  acc_no?: string | null;
  pan_no?: string | null;
  aadhar_no?: string | null;
  passport_no?: string | null;
  basic_pay?: number | null;
  bank_name?: string | null;
  portal_email?: string | null;
  designation?: string | null;
};

export type InsytixPayslip = {
  employee_id?: string | null;
  org_user_id?: string | null;
  name?: string | null;
  email?: string | null;
  designation?: string | null;
  doj?: string | null;
  acc_no?: string | null;
  bank_name?: string | null;
  basic_pay: number;
  working_days: number;
  paid_days: number;
  lop_days: number;
  lop_amount: number;
  net_pay: number;
  month: string;
  linked: boolean;
};

export type InsytixEmployeeInput = {
  name: string;
  dob?: string;
  doj?: string;
  p_mail?: string;
  c_mail?: string;
  acc_no?: string;
  bank_name?: string;
  basic_pay?: string;
  pan_no?: string;
  aadhar_no?: string;
  passport_no?: string;
};

const request = async (method: string, path: string, body?: object) => {
  const res = await fetch(`${API_XBI4_URL}${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.error || "Request failed");
  }
  return json;
};

export const getLoginEmail = (auth: any) =>
  auth?.userAttr?.email ||
  auth?.user?.attributes?.email ||
  auth?.idToken?.payload?.email ||
  auth?.user?.signInUserSession?.idToken?.payload?.email ||
  auth?.userInfo?.email ||
  "";

export const getInsytixAccess = (orgId: string | number, email: string) =>
  request(
    "GET",
    `/orgs/${orgId}/insytix/access?email=${encodeURIComponent(email)}`
  ) as Promise<{ is_admin: number }>;

const normalName = (value?: string | null) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isInsytixEmployee = (employeeId?: string | null) =>
  String(employeeId || "").startsWith("INSYTIX");

const cleanField = (value?: string | null) => {
  const text = String(value ?? "").trim();
  if (!text || text.toLowerCase() === "none" || text.toLowerCase() === "null") return "";
  return text;
};

export const getInsytixEmployees = async (orgId: string | number) => {
  const [json, portalUsers] = await Promise.all([
    request("GET", `/orgs/${orgId}/employee`),
    getUsers(orgId),
  ]);
  const rows = (Array.isArray(json) ? json : []).filter((row) =>
    isInsytixEmployee(row?.employee_id)
  );
  return rows.map((row) => {
    const fullName = normalName(row?.Name);
    const match = portalUsers.find((person) => {
      if (row?.org_user_id && person?.org_user_id === row.org_user_id) return true;
      const portalName = normalName(
        `${person?.first_name || ""} ${person?.middle_name || ""} ${person?.last_name || ""}`
      );
      return fullName && portalName === fullName;
    });
    return {
      ...row,
      org_user_id: row?.org_user_id || match?.org_user_id || null,
      c_mail: cleanField(row?.c_mail),
      p_mail: cleanField(row?.p_mail),
      portal_email: cleanField(row?.c_mail),
      bank_name: cleanField(row?.bank_name),
      designation: cleanField(row?.designation) || cleanField(row?.github),
      first_name: match?.first_name,
      last_name: match?.last_name,
    } as InsytixEmployee;
  });
};

export const addEmployeeWithPortalLogin = async (
  orgId: string | number,
  input: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    role_id?: string;
    leadOrgId?: string;
    dob?: string;
    doj?: string;
  }
) => {
  const portal = await createUser(orgId, {
    first_name: input.first_name,
    last_name: input.last_name,
    email: input.email,
    phone: input.phone,
    designation_id: input.role_id,
    role_id: input.role_id,
    leadOrgId: input.leadOrgId,
  });
  const name = `${input.first_name} ${input.last_name}`.trim();
  try {
    await createInsytixEmployee(orgId, {
      name,
      dob: input.dob || "",
      doj: input.doj || "",
      c_mail: input.email,
    });
  } catch (error) {
    if (portal?.org_user_id) {
      await fetch(
        `${API_XBI4_URL}/orgs/${orgId}/users/${portal.org_user_id}/userdetails`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            dob: input.dob || "",
            doj: input.doj || "",
            p_email: "",
            c_email: input.email,
            acc_number: "",
            pan_no: "",
            aadhar_no: "",
            passport_no: "",
            linkedin: "",
            github: "",
            facebook: "",
          }),
        }
      );
    }
  }
  if (portal?.org_user_id) {
    await request("POST", `/orgs/${orgId}/employee`, {
      name,
      org_user_id: portal.org_user_id,
      dob: input.dob || "",
      doj: input.doj || "",
      c_mail: input.email,
    }).catch(() => undefined);
  }
  return portal;
};

const roundDays = (value: number) => Math.round((Number(value) || 0) * 100) / 100;

const roundMoney = (value: number) => Math.round((Number(value) || 0) * 100) / 100;

const parseDay = (value?: string | null) => {
  const text = String(value || "").trim().slice(0, 10);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const dmy = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  return null;
};

const dayUnits = (day: Date) => {
  const weekday = day.getDay();
  if (weekday === 0) return 0;
  if (weekday === 6) return 0.5;
  return 1;
};

const eachDay = (start: Date, end: Date) => {
  const days: Date[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cursor <= last) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

export const latestReleasedMonth = (now = new Date()) => {
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${previous.getFullYear()}-${String(previous.getMonth() + 1).padStart(2, "0")}`;
};

export const payslipReleaseDate = (month: string) => {
  const [year, mon] = month.split("-").map(Number);
  return new Date(year, mon, 1);
};

export const isPayslipReleased = (month: string, now = new Date()) => {
  if (!/^\d{4}-\d{2}$/.test(month)) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return today >= payslipReleaseDate(month);
};

const monthBounds = (month: string) => {
  const [year, mon] = month.split("-").map(Number);
  return {
    start: new Date(year, mon - 1, 1),
    end: new Date(year, mon, 0),
  };
};

const leaveUnits = (
  startValue: string,
  endValue: string,
  duration: unknown,
  monthStart: Date,
  monthEnd: Date
) => {
  const start = parseDay(startValue);
  let end = parseDay(endValue) || start;
  if (!start || !end) return 0;
  if (end < start) end = start;
  const span = eachDay(start, end);
  if (span.length === 1) {
    const day = span[0];
    if (day < monthStart || day > monthEnd) return 0;
    const weight = dayUnits(day);
    const parsed = Number(duration);
    const recorded =
      duration == null || duration === "" || !Number.isFinite(parsed) ? weight : parsed;
    if (!weight || !(recorded > 0)) return 0;
    return Math.min(recorded, weight);
  }
  return span.reduce((total, day) => {
    if (day < monthStart || day > monthEnd) return total;
    return total + dayUnits(day);
  }, 0);
};

const isLossOfPay = (leave: any) => {
  const id = Number(leave?.leave_type_id);
  const name = String(leave?.leave_type_name || leave?.leave_type || "").toLowerCase();
  return id === 6 || name.includes("loss of pay") || name === "lop";
};

const collectLeaves = (payload: any) => {
  const rows = Array.isArray(payload) ? payload : payload ? [payload] : [];
  const leaves: any[] = [];
  rows.forEach((row) => {
    if (Array.isArray(row?.history)) leaves.push(...row.history);
    else if (row?.leave_type_id != null || row?.start_date || row?.leave_start_date) {
      leaves.push(row);
    }
  });
  return leaves.filter(isLossOfPay);
};

const money = (value: unknown) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

type ProdInsytixEmployee = {
  employee_id?: string;
  bank_name?: string | null;
  acc_no?: string | null;
  basic_pay?: number | null;
  designation?: string | null;
};

const readProdInsytixEmployee = async (
  employeeId: string
): Promise<ProdInsytixEmployee | null> => {
  try {
    const res = await fetch(
      `/insytix-db/employee?employee_id=${encodeURIComponent(employeeId)}`
    );
    if (!res.ok) return null;
    const row = await res.json();
    if (!row || row.error) return null;
    return row;
  } catch {
    return null;
  }
};

export const loadLoginPayslip = async (
  orgId: string | number,
  email: string,
  month: string,
  orgUserId?: string | number | null
): Promise<InsytixPayslip> => {
  if (!isPayslipReleased(month)) {
    const release = payslipReleaseDate(month).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    throw new Error(`This payslip is available on ${release}.`);
  }

  const json = await request("GET", `/orgs/${orgId}/employee`);
  const rows = Array.isArray(json) ? json : [];
  const wantedEmail = String(email || "").trim().toLowerCase();
  const wantedOrg = orgUserId != null && orgUserId !== "" ? String(orgUserId) : "";
  const matches = rows.filter((row) => {
    const mail = String(row?.c_mail || "").trim().toLowerCase();
    const org = String(row?.org_user_id || "");
    return (wantedOrg && org === wantedOrg) || (wantedEmail && mail === wantedEmail);
  });
  const employee =
    matches.find((row) => isInsytixEmployee(row?.employee_id)) ||
    matches.find((row) => isInsytixEmployee(row?.insytix_employee_id)) ||
    matches[0] ||
    null;

  const resolvedOrg = employee?.org_user_id || wantedOrg;
  let history: any = [];
  if (resolvedOrg) {
    history = await getOrgUserLeaveHistory(orgId, String(resolvedOrg));
  }

  const prod = employee?.employee_id
    ? await readProdInsytixEmployee(String(employee.employee_id))
    : null;

  let basic = money(prod?.basic_pay) || money(employee?.basic_pay);
  if (!basic && employee?.employee_id) {
    const pay = await getIndEmployeePay(orgId, employee.employee_id);
    const record = Array.isArray(pay) ? pay[0] : pay;
    basic = money(record?.basic_pay);
  }

  const { start, end } = monthBounds(month);
  const joined = parseDay(employee?.doj);
  let payableStart = start;
  if (joined && joined > end) payableStart = new Date(end.getTime() + 86400000);
  else if (joined && joined > start) payableStart = joined;
  const workingDays = roundDays(
    payableStart > end ? 0 : eachDay(payableStart, end).reduce((total, day) => total + dayUnits(day), 0)
  );
  const lopDays = roundDays(
    Math.min(
      collectLeaves(history).reduce(
        (total, leave) =>
          total +
          leaveUnits(
            leave?.start_date || leave?.leave_start_date,
            leave?.end || leave?.end_date || leave?.leave_end_date,
            leave?.duration,
            start,
            end
          ),
        0
      ),
      workingDays
    )
  );
  const paidDays = roundDays(Math.max(workingDays - lopDays, 0));
  const lopAmount = workingDays ? roundMoney((basic / workingDays) * lopDays) : 0;

  return {
    employee_id:
      cleanField(employee?.insytix_employee_id) ||
      cleanField(prod?.employee_id) ||
      cleanField(employee?.employee_id) ||
      null,
    org_user_id: resolvedOrg || null,
    name: cleanField(employee?.Name) || null,
    email: cleanField(employee?.c_mail) || email || null,
    designation:
      cleanField(prod?.designation) ||
      cleanField(employee?.designation) ||
      cleanField(employee?.github) ||
      null,
    doj: cleanField(employee?.doj) || null,
    acc_no: cleanField(prod?.acc_no) || cleanField(employee?.acc_no) || null,
    bank_name: cleanField(prod?.bank_name) || cleanField(employee?.bank_name) || null,
    basic_pay: roundMoney(basic),
    working_days: workingDays,
    paid_days: paidDays,
    lop_days: lopDays,
    lop_amount: lopAmount,
    net_pay: roundMoney(basic - lopAmount),
    month,
    linked: Boolean(employee?.employee_id),
  };
};

export const saveEmployeeBasicPay = async (
  orgId: string | number,
  employeeId: string,
  basicPay: string | number
) => {
  const current = await getIndEmployeePay(orgId, employeeId);
  const record = Array.isArray(current) ? current[0] || {} : {};
  const amount = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return request("PATCH", `/orgs/${orgId}/employee/${employeeId}/payments`, {
    basic_pay: amount(basicPay),
    vpr: amount(record?.vpr),
    epf: amount(record?.epf),
    esi: amount(record?.esi),
    tax: amount(record?.tax),
  });
};

export const saveEmployeeProfile = async (
  orgId: string | number,
  orgUserId: string,
  data: InsytixEmployeeInput & { linkedin?: string; github?: string; facebook?: string }
) =>
  request("POST", `/orgs/${orgId}/users/${orgUserId}/userdetails`, {
    name: data.name,
    dob: data.dob || "",
    doj: data.doj || "",
    p_email: data.p_mail || "",
    c_email: data.c_mail || "",
    acc_number: data.acc_no || "",
    pan_no: data.pan_no || "",
    aadhar_no: data.aadhar_no || "",
    passport_no: data.passport_no || "",
    linkedin: data.linkedin || "",
    github: data.github || "",
    facebook: data.facebook || "",
    bank_name: data.bank_name || "",
  });

export const createInsytixEmployee = (
  orgId: string | number,
  data: InsytixEmployeeInput
) => request("POST", `/orgs/${orgId}/insytix/employees`, data);

export const updateInsytixEmployee = (
  orgId: string | number,
  employeeId: string,
  data: InsytixEmployeeInput
) => request("PATCH", `/orgs/${orgId}/insytix/employees/${employeeId}`, data);

export const deleteInsytixEmployee = (
  orgId: string | number,
  employeeId: string
) => request("DELETE", `/orgs/${orgId}/insytix/employees/${employeeId}`);
